/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */
import React from 'react'
import clsx from 'clsx'

interface UiTableContainerProps {
    className?: string
    children: React.ReactNode
    flat?: boolean
}

export const UiTableContainer: React.FC<UiTableContainerProps> = ({
    className,
    children,
    flat,
}) => {
    return (
        <div
            className={clsx(
                'overflow-x-auto w-full bg-white dark:bg-gray-950',
                !flat &&
                    'border border-gray-200 dark:border-gray-800 rounded-lg',
                className
            )}
        >
            {children}
        </div>
    )
}

interface UiTableProps {
    children: React.ReactNode
    striped?: boolean
    hasBorderTop?: boolean
    className?: string
}

export const UiTable: React.FC<UiTableProps> = ({
    children,
    striped,
    hasBorderTop,
    className,
}) => {
    return (
        <table
            className={clsx(
                'UiTable min-w-full w-full border-collapse',
                'divide-y divide-gray-200 dark:divide-gray-800',
                striped &&
                    '[&_tbody_tr:nth-child(even)]:bg-gray-50/50 dark:[&_tbody_tr:nth-child(even)]:bg-gray-900/50',
                hasBorderTop && 'border-t border-gray-200 dark:border-gray-800',
                className
            )}
        >
            {children}
        </table>
    )
}

interface UiTableHeadProps {
    children: React.ReactNode
}

export const UiTableHeadRowLess: React.FC<UiTableHeadProps> = ({
    children,
}) => {
    return (
        <thead
            className={clsx(
                'text-xs font-medium sticky top-0 z-20',
                'bg-gray-50 text-gray-600 uppercase tracking-wider',
                'dark:bg-gray-900 dark:text-gray-300'
            )}
        >
            {children}
        </thead>
    )
}

export const UiTableHead: React.FC<UiTableHeadProps> = ({ children }) => {
    return (
        <thead
            className={clsx(
                'bg-gray-50 text-gray-600 uppercase tracking-wider sticky top-0 z-20',
                'dark:bg-gray-900 dark:text-gray-300'
            )}
        >
            <tr className={clsx('text-xs font-medium')}>{children}</tr>
        </thead>
    )
}

interface UiTableHeaderCellProps {
    align?: 'left' | 'right' | 'center'
    children?: React.ReactNode
    className?: string
	colSpan?: number
}

export const UiTableHeaderCell: React.FC<UiTableHeaderCellProps> = ({
    align = 'left',
    children,
    className,
	colSpan = 1
}) => {
    return (
        <th colSpan={colSpan}
            className={clsx(
                'px-3 py-2',
                align === 'left' && 'text-left',
                align === 'right' && 'text-right',
                align === 'center' && 'text-center',
                className
            )}
        >
            {children}
        </th>
    )
}

interface UiTableBodyProps {
    children: React.ReactNode
    className?: string
}

export const UiTableBody: React.FC<UiTableBodyProps> = ({ children, className }) => {
    return (
        <tbody
            className={clsx(
                'bg-white divide-y divide-gray-200',
                'dark:bg-gray-950 dark:divide-gray-800',
                className
            )}
        >
            {children}
        </tbody>
    )
}

interface UiTableRowProps {
    children: React.ReactNode
    onClick?: () => void
    className?: string
}

export const UiTableRow: React.FC<UiTableRowProps> = ({
    children,
    onClick,
    className,
}) => {
    return (
        <tr
            className={clsx(
                'hover:bg-gray-50',
                'dark:hover:bg-gray-900',
                onClick && 'cursor-pointer',
                className
            )}
            onClick={onClick}
        >
            {children}
        </tr>
    )
}

interface UiTableCellProps {
    align?: 'left' | 'right' | 'center'
    children: React.ReactNode | React.ReactNode[]
    className?: string
    colSpan?: number
    fontWeight?: 'normal' | 'medium' | 'semibold' | 'bold'
}

export const UiTableCell: React.FC<UiTableCellProps> = ({
    align = 'left',
    children,
    className,
    colSpan,
    fontWeight = 'normal',
}) => {
    return (
        <td
            className={clsx(
                'px-3 py-2 align-middle',
                'text-gray-900 dark:text-gray-100',
                align === 'left' && 'text-left',
                align === 'right' && 'text-right',
                align === 'center' && 'text-center',
                fontWeight === 'normal' && 'font-normal',
                fontWeight === 'medium' && 'font-medium',
                fontWeight === 'semibold' && 'font-semibold',
                fontWeight === 'bold' && 'font-bold',
                className
            )}
            colSpan={colSpan}
        >
            {children}
        </td>
    )
}

interface UiTableActionsCellProps {
    children: React.ReactNode
    className?: string
}

export const UiTableActionsCell: React.FC<UiTableActionsCellProps> = ({
    children,
    className,
}) => {
    return (
        <td
            className={clsx(
                'px-3 py-2 whitespace-nowrap text-right font-medium align-middle',
                className
            )}
        >
            <div className={clsx('flex justify-end gap-2 items-center')}>{children}</div>
        </td>
    )
}

export default UiTable
